<script setup lang="ts">
import { Loader2, RefreshCw } from 'lucide-vue-next'
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '@/components/ui/accordion'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Badge } from '@/components/ui/badge'
import type { AvailabilityResult, ECResult } from '@/types/validation'
import type { ECSearchResult } from '@/composables/useECSearchStream'
import { Button } from '@/components/ui/button'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import calendar from 'dayjs/plugin/calendar'
import AvailabilityTreeMap from './AvailabilityTreeMap.vue'
import PriceQuantityScatter from './PriceQuantityScatter.vue'
import AvailabilityEulerDiagram from './AvailabilityEulerDiagram.vue'
import { useElementSize } from '@vueuse/core'
import { useValidation } from '@/composables/useValidation'
import { useValidationTransformers } from '@/composables/useValidationTransformers'
import { useECSearchStream } from '@/composables/useECSearchStream'
import { storeToRefs } from 'pinia'
import { useProjectStore } from '@/stores/projectStore'
import { useValidationResultsStore } from '@/stores/validationResultsStore'
import type { MitterEvents } from '@/types/mitterEvents'
import { toolDefinitions } from '@/config/tools'
import { useIntersectionObserver } from '@vueuse/core'
import { markRaw } from 'vue'


dayjs.extend(relativeTime)
dayjs.extend(calendar)

// Initialize core services
const mitter = useMitter()
const projectStore = useProjectStore()
const validationResultsStore = useValidationResultsStore()
const { selectedProject } = storeToRefs(projectStore)
const { validateAvailability, validationLoadingState } = useValidation()
const { validateData } = useValidationTransformers()

useSquircleObserver()

// Initialize EC search stream
const { 
  isSearching, 
  progress: searchProgress, 
  finalResult: ecSearchResult, 
  error: searchError,
  latestProgress,
  searchStats,
  startSearch,
  stopSearch,
  clearProgress 
} = useECSearchStream()

// Component props
const props = defineProps<{
  data?: string[],
  metadata?: any,
  cell?: any,
  panel_id?: string,
  project_id?: string
}>()

// Define emits
const emit = defineEmits(['results'])

// Component state
const showHide = ref({
  available: true,
  unavailable: true
})

const speciesNames = ref<string[]>([])
const validationResults = ref<Record<string, AvailabilityResult>>({})
const isRefreshing = ref(false)
const tool = ref(toolDefinitions.find(t => t.id === 'availabilityValidation'))

// Panel-scoped validation results
const panelValidationResults = ref<Record<string, {
  results: Record<string, AvailabilityResult>
  speciesNames: string[]
  timestamp: number
}>>({})

const activeTabPanel = ref<string>('')

// Cache status
const cacheStatus = computed(() => {
  if (!selectedProject.value?.id || speciesNames.value.length === 0) {
    return { hasCachedResults: false, cacheAge: null }
  }
  
  const cachedResults = validationResultsStore.getAvailabilityResults(
    speciesNames.value, 
    selectedProject.value.id.toString(),
    86400,
    props.panel_id
  )
  
  if (!cachedResults) {
    return { hasCachedResults: false, cacheAge: null }
  }
  
  const ageInSeconds = (Date.now() - cachedResults.timestamp) / 1000
  return { 
    hasCachedResults: true, 
    cacheAge: Math.round(ageInSeconds),
    cacheKey: validationResultsStore.hashSpeciesNames(speciesNames.value, selectedProject.value.id.toString())
  }
})

const clearCache = () => {
  if (selectedProject.value?.id) {
    console.log('[AvailabilityStatus] Clearing availability cache')
    validationResults.value = {}
    // Note: We could add a method to clear specific cache entries if needed
    // For now, user can call validationResultsStore.clearAvailabilityResults() manually
  }
}


// Handle validation data from panels
mitter.listen('panel:data:validate', async (event: MitterEvents['panel:data:validate']) => {
  if (event.validation_tool_uid !== tool.value?.uid) return;
  
  console.log('[AvailabilityStatus] Received panel:data:validate event', event);
  
  const validationResult = await validateData(event.data, event.tool_uid);
  console.log('[AvailabilityStatus] Validation result', validationResult);
  
  // Update species names from validation result if available
  if (validationResult.fixedData?.speciesNames) {
    speciesNames.value = validationResult.fixedData.speciesNames;
    
    // If we have AI-provided size recommendations, store them for later use
    if (validationResult.fixedData.sizeRecommendations) {
      console.log('[AvailabilityStatus] Using AI size recommendations:', 
        validationResult.fixedData.sizeRecommendations);
    }
    
    // Trigger validation if we have species names
    if (speciesNames.value.length > 0 && selectedProject.value?.id) {
      refresh(true);
    }
  }
});

const refresh = async (useCache: boolean = true, maxCacheAge: number = 86400, limit: number = 100) => {
  console.log('[AvailabilityStatus] refresh() called with useCache:', useCache, 'speciesNames:', speciesNames.value.length)
  
  if (!selectedProject.value?.id || speciesNames.value.length === 0) {
    console.log('[AvailabilityStatus] Refresh aborted - missing project or species names')
    return
  }
  
  const projectId = selectedProject.value.id.toString()
  
  // Check cache first if useCache is true
  if (useCache) {
    const cachedResults = validationResultsStore.getAvailabilityResults(
      speciesNames.value, 
      projectId, 
      maxCacheAge,
      props.panel_id
    )
    
    if (cachedResults) {
      console.log('[AvailabilityStatus] Using cached availability results')
      validationResults.value = cachedResults.results
      emit('results', { result: cachedResults.results })
      return
    }
  }
  
  console.log('[AvailabilityStatus] Starting fresh search...')
  isRefreshing.value = true
  
  // Clear previous search state
  clearProgress()

  console.log('[AvailabilityStatus] Starting streaming search for species:', speciesNames.value)
  console.log('[AvailabilityStatus] isSearching before startSearch:', isSearching.value)
  
  // Transform species names to search requests
  const searches = speciesNames.value.map(plantName => ({
    plantName,
    state: selectedProject.value?.project_location?.state,
    region: selectedProject.value?.project_location?.region
  }))
  
  console.log('[AvailabilityStatus] About to call startSearch with:', searches.length, 'searches')
  
  // Start streaming search
  await startSearch({
    searches,
    useCache: !useCache ? false : useCache, // Force fresh if useCache is false
    maxCacheAge,
    limit
  })
  
  console.log('[AvailabilityStatus] startSearch completed, isSearching after:', isSearching.value)
  
  isRefreshing.value = false
}

// Apply validated results back to the panel
const applyValidatedResults = async () => {
  console.log('[AvailabilityStatus] Applying validated results')
  
  if (props.panel_id) {
    // mitter.emit('panel:data:apply', {
    //   panel_id: props.panel_id,
    //   data: {
    //     type: 'availability',
    //     results: validationResults.value
    //   }
    // })
  }
}

// Watch for EC search results
watch(() => ecSearchResult.value, (newResult) => {
  console.log('[AvailabilityStatus] EC search result watcher triggered. newResult:', !!newResult, 'success:', newResult?.success, 'projectId:', !!selectedProject.value?.id)
  console.log('[AvailabilityStatus] Full newResult object:', newResult)
  console.log('[AvailabilityStatus] Has results array:', newResult && 'results' in newResult && Array.isArray(newResult.results))
  console.log('[AvailabilityStatus] Results length:', newResult?.results?.length)
  
  if (newResult && newResult.success && selectedProject.value?.id) {
    console.log('[AvailabilityStatus] Processing EC search results:', newResult)
    
    // Transform EC search results to availability results format
    const availabilityResults: Record<string, AvailabilityResult> = {}
    
    if ('results' in newResult && Array.isArray(newResult.results)) {
      console.log('[AvailabilityStatus] Processing multi-search results, count:', newResult.results.length)
      // Multi-search result
      newResult.results.forEach((searchResult: ECSearchResult, index: number) => {
        console.log(`[AvailabilityStatus] Processing search result ${index}:`, searchResult)
        console.log(`[AvailabilityStatus] Search success: ${searchResult.success}, results count: ${searchResult.results?.length}`)
        
        if (searchResult.success && searchResult.results?.length > 0) {
          // Extract plant name from the actual results (botanical_name)
          const plantName = searchResult.results[0]?.botanical_name || 
                           speciesNames.value[index] || 
                           `Unknown Plant ${index + 1}`
          
          console.log(`[AvailabilityStatus] Processing results for: ${plantName}`, searchResult.results.length, 'suppliers')
          console.log(`[AvailabilityStatus] First result sample:`, searchResult.results[0])
          
          availabilityResults[plantName] = {
            isAvailable: searchResult.results.length > 0,
            suppliers: searchResult.results.map((result: any) => ({
              botanical_name: result.botanical_name || plantName,
              common_name: result.common_name || result.botanical_name || plantName,
              supplier: {
                name: result.supplier?.name || result.supplier || 'Unknown Supplier',
                state: result.supplier?.state || 'Unknown',
                address: result.supplier?.address || '',
                phone: result.supplier?.phone || '',
                website: result.supplier?.website || ''
              },
              price: result.price || 0,
              size: result.size || 'Unknown',
              quantity: result.quantity || 0,
              batch_id: result.batch_id || '',
              last_updated: result.last_updated || new Date().toISOString()
            })),
            source: 'EC Search Stream',
            lastChecked: searchResult.metadata.timestamp
          }
        } else {
          // Handle unsuccessful searches (no results found)
          const plantName = speciesNames.value[index] || `Unknown Plant ${index + 1}`
          console.log(`[AvailabilityStatus] No results found for: ${plantName}`)
          
          availabilityResults[plantName] = {
            isAvailable: false,
            suppliers: [],
            source: 'EC Search Stream',
            lastChecked: searchResult.metadata?.timestamp || new Date().toISOString()
          }
        }
      })
    } else if (newResult.results?.length > 0) {
      // Single search result
      const plantName = speciesNames.value[0] || 'Unknown'
      availabilityResults[plantName] = {
        isAvailable: newResult.results.length > 0,
        suppliers: newResult.results.map((result: any) => ({
          botanical_name: result.botanical_name || plantName,
          common_name: result.common_name || plantName,
          supplier: {
            name: result.supplier || 'Unknown Supplier',
            state: result.state || 'Unknown',
            address: result.address || '',
            phone: result.phone || '',
            website: result.website || ''
          },
          price: result.price || 0,
          size: result.size || 'Unknown',
          quantity: result.quantity || 0,
          batch_id: result.batch_id || '',
          last_updated: result.last_updated || new Date().toISOString()
        })),
        source: 'EC Search Stream',
        lastChecked: newResult.metadata.timestamp
      }
    }
    
    // Update validation results
    console.log('[AvailabilityStatus] About to set validation results:', availabilityResults)
    console.log('[AvailabilityStatus] availabilityResults keys:', Object.keys(availabilityResults))
    console.log('[AvailabilityStatus] availabilityResults values:', Object.values(availabilityResults))
    
    validationResults.value = availabilityResults
    
    console.log('[AvailabilityStatus] After setting - validationResults.value:', validationResults.value)
    console.log('[AvailabilityStatus] Available species count:', Object.values(availabilityResults).filter(r => r.isAvailable).length)
    console.log('[AvailabilityStatus] Total species processed:', Object.keys(availabilityResults).length)
    
    // Cache the results
    validationResultsStore.setAvailabilityResults(
      speciesNames.value,
      selectedProject.value.id.toString(),
      availabilityResults,
      props.panel_id
    )
    
    // Emit results via both Vue emit and mitter for grid coloring
    emit('results', { result: availabilityResults })

    // Emit availability validation results for grid coloring
    console.log('[AvailabilityStatus] Emitting availability validation results via mitter:', availabilityResults)
    mitter.emit('availability:validation:results', availabilityResults)
  }
})

// Add debug watchers for search states
watch(() => isSearching.value, (newVal) => {
  console.log('[AvailabilityStatus] isSearching changed:', newVal)
})

watch(() => latestProgress.value, (newVal) => {
  console.log('[AvailabilityStatus] latestProgress changed:', newVal)
})

watch(() => searchError.value, (newVal) => {
  console.log('[AvailabilityStatus] searchError changed:', newVal)
})

// Debug watcher for validationResults  
watch(() => validationResults.value, (newVal, oldVal) => {
  console.log('[AvailabilityStatus] validationResults changed!')
  console.log('[AvailabilityStatus] New value keys:', Object.keys(newVal || {}))
  console.log('[AvailabilityStatus] Old value keys:', Object.keys(oldVal || {}))
  console.log('[AvailabilityStatus] Has results:', Object.keys(newVal || {}).length > 0)
}, { deep: true, immediate: true })

// Watch for changes in species names to check cache
watch(() => speciesNames.value, (newSpeciesNames, oldSpeciesNames) => {
  if (!selectedProject.value?.id) return
  
  // Only proceed if we have species names and they've actually changed
  if (newSpeciesNames.length > 0 && JSON.stringify(newSpeciesNames) !== JSON.stringify(oldSpeciesNames)) {
    console.log('[AvailabilityStatus] Species names changed, checking cache...')
    
    // Check if we have cached results for this species list
    const cachedResults = validationResultsStore.getAvailabilityResults(
      newSpeciesNames, 
      selectedProject.value.id.toString(),
      86400,
      props.panel_id
    )
    
    if (cachedResults) {
      console.log('[AvailabilityStatus] Found cached results for updated species list')
      validationResults.value = cachedResults.results
      emit('results', { result: cachedResults.results })
    } else {
      console.log('[AvailabilityStatus] No cached results found, will need fresh data')
      // Clear existing results since we don't have cache for new species list
      validationResults.value = {}
    }
  }
}, { immediate: false })

// Initialize on mount
onMounted(() => {
  console.log('[AvailabilityStatus] Component mounted')
  
  // If we have initial data, check cache first
  if (props.data && props.data.length > 0) {
    speciesNames.value = props.data
    // The watcher will handle checking cache and triggering refresh if needed
  }
})



const getAvailableSpecies = computed(() => {
  return Object.entries(validationResults.value)
    .filter(([_, result]) => result.isAvailable)
    .map(([name, result]) => ({ name, result }))
})

const getUnavailableSpecies = computed(() => {
  return Object.entries(validationResults.value)
    .filter(([_, result]) => !result.isAvailable)
    .map(([name, result]) => ({ name, result }))
})

const formatPrice = (price: number) => {
  return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' }).format(price)
}

const formatDate = (date: string | Date) => {
  return dayjs(date).fromNow()
}

// Add this computed property for tabulator columns
const availableColumns = computed(() => [
  {
    title: "Species",
    field: "name",
    headerSort: true,
    width: 200
  },
  {
    title: "Supplier",
    field: "supplier",
    headerSort: true,
  },
  {
    title: "State",
    field: "state",
    headerSort: true,
    width: 100
  },
  {
    title: "Price",
    field: "price",
    headerSort: true,
    formatter: (cell: any) => formatPrice(cell.getValue()),
    width: 100
  },
  {
    title: "Size",
    field: "size",
    headerSort: true,
    width: 100
  },
  {
    title: "Quantity",
    field: "quantity",
    headerSort: true,
    width: 100
  },
  {
    title: "Last Updated",
    field: "lastUpdated",
    headerSort: true,
    formatter: (cell: any) => formatDate(cell.getValue()),
    width: 150
  }
])

// Add this computed property to format data for the table
const availableTableData = computed(() => {
  return getAvailableSpecies.value.flatMap(({ name, result }) => 
    result.suppliers?.map(supplier => ({
      name,
      supplier: supplier.supplier.name,
      state: supplier.supplier.state,
      price: supplier.price,
      size: supplier.size,
      quantity: supplier.quantity,
      lastUpdated: supplier.last_updated
    })) || []
  )
})

const selectedSpecies = ref<{
  name: string,
  price?: number,
  size?: string,
  state?: string,
  value?: number,
  parentName?: string
}>({ name: '' })

const handleTreeMapSelect = (data: any) => {
  if (data.price !== undefined) {
    selectedSpecies.value = {
      ...data,
      parentName: data.parent
    }
  } else {
    selectedSpecies.value = {
      name: data.name
    }
  }
}

// Add section refs and active section tracking
const activeSection = ref('treemap')
const treemapSection = ref<HTMLElement | null>(null)
const summarySection = ref<HTMLElement | null>(null)
const eulerSection = ref<HTMLElement | null>(null)
const scatterSection = ref<HTMLElement | null>(null)

// Add scroll area ref
const scrollAreaRef = ref<InstanceType<typeof ScrollArea> | null>(null)

// Add container ref and size tracking
const tabsContainer = ref<HTMLElement | null>(null)
const { width: containerWidth } = useElementSize(tabsContainer)

// Add computed for narrow width
const isNarrowWidth = computed(() => containerWidth.value < 300)

// Add computed for availability statistics
const availabilityStats = computed(() => {
  const totalSpecies = Object.keys(validationResults.value).length
  const availableCount = getAvailableSpecies.value.length
  const unavailableCount = getUnavailableSpecies.value.length
  
  return {
    totalSpecies,
    availableCount,
    unavailableCount,
    availablePercentage: totalSpecies > 0 ? Math.round((availableCount / totalSpecies) * 100) : 0
  }
})

// Add sections data
const sections = computed(() => [
  { 
    id: 'treemap', 
    label: "Availability",
    score: validationResults.value ? Object.keys(validationResults.value).length : null
  },
  {
    id: 'summary',
    label: "Summary",
    score: validationResults.value ? availabilityStats.value.availableCount : null
  },
  { 
    id: 'euler', 
    label: "Relationships",
    score: validationResults.value ? 
      Object.values(validationResults.value)
        .filter(result => result.isAvailable && result.suppliers && result.suppliers.length > 0).length 
      : null
  },
  { 
    id: 'scatter', 
    label: "Analysis",
    score: validationResults.value ? 
      Object.values(validationResults.value)
        .reduce((acc, curr) => acc + (curr.suppliers?.length || 0), 0) 
      : null
  }
])

// Add scroll to section function
const scrollToSection = (sectionId: string) => {
  const section = document.getElementById(sectionId)
  const viewport = document.querySelector('[data-radix-scroll-area-viewport]')
  if (section && viewport) {
    const targetScroll = section.offsetTop - 120
    viewport.scrollTop = targetScroll
    activeSection.value = sectionId
  }
}

// Add intersection observer setup
onMounted(() => {
  // Simplified intersection observer without complex types
  nextTick(() => {
    const sections = ['treemap', 'summary', 'euler', 'scatter']
    
    sections.forEach(id => {
      const element = document.getElementById(id)
      if (element) {
        useIntersectionObserver(
          element,
          ([{ isIntersecting }]) => {
            if (isIntersecting) {
              activeSection.value = id
            }
          },
          { 
            threshold: [0.1, 0.5, 0.9],
            rootMargin: '-100px 0px 0px 0px'
          }
        )
      }
    })
  })
})
</script>

<template>
  <div class="flex flex-col h-full">


    <!-- Section Tabs -->
    <div v-if="validationResults && Object.keys(validationResults).length > 0"
      ref="tabsContainer"
      class="sticky sticky-header top-0 z-10 bg-background/80 backdrop-blur-sm border-b mb-0 rounded-full m-4 mx-5 mb-4"
    >
      <div 
        class="flex gap-2 p-1.5 w-full overflow-x-hidden rounded-full"
        :class="isNarrowWidth ? 'justify-center' : 'justify-evenly'"
      >
        <button
          v-for="section in sections" 
          :key="section.id"
          v-show="!isNarrowWidth || activeSection === section.id"
          @click.stop="scrollToSection(section.id)"
          class="relative pl-2 pr-1.5 py-1.5 text-sm font-medium transition-all duration-300 rounded-full min-w-[120px] text-center"
          :class="[
            activeSection === section.id
              ? 'text-background bg-primary shadow-sm' 
              : 'text-muted-foreground hover:text-foreground/80'
          ]"
        >
          <div class="flex items-center justify-between">
            <p class="truncate overflow-hidden h-5">{{ section.label }}</p>
            <Badge 
              v-if="section.score"
              :variant="activeSection === section.id ? 'secondary' : 'outline'"
              class="text-[0.8em] py-0 px-1 ml-1 border-none ring-0"
            >
              {{ section.score }}
            </Badge>
          </div>
        </button>
      </div>
    </div>

    <!-- Streaming Messages -->
    <div v-if="isSearching || validationLoadingState === 'pending' || searchError" class="px-6 py-4 space-y-3">
      
      
      <!-- Loading state -->
      <div 
        v-if="isSearching || validationLoadingState === 'pending'" 
        v-motion-fade-visible
        class="flex items-center gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg"
      >
        <div class="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full flex-shrink-0"></div>
        <div class="flex flex-col min-w-0">
          <span v-if="isSearching" class="text-sm font-medium text-blue-800">
            Searching availability for {{ speciesNames.length }} species...
          </span>
          <span v-else class="text-sm font-medium text-blue-800">
            Validating availability for {{ speciesNames.length }} species...
          </span>
          
          <!-- Show latest progress if available -->
          <div 
            v-if="latestProgress && latestProgress.message" 
            v-motion-fade-visible
            class="text-xs text-blue-600 mt-1 truncate"
          >
            {{ latestProgress.message }}
          </div>
        </div>
      </div>

      <!-- Error state -->
      <div 
        v-if="searchError" 
        v-motion-fade-visible
        class="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg"
      >
        <Icon name="lucide:alert-triangle" class="w-4 h-4 text-red-500 flex-shrink-0" />
        <span class="flex-1 text-red-700 font-medium min-w-0 truncate">Search Error: {{ searchError }}</span>
        <Button size="sm" variant="outline" @click="clearProgress" class="border-red-200 hover:bg-red-50 flex-shrink-0">
          Clear
        </Button>
      </div>
    </div>

    <!-- No Results State -->
    <div v-if="(!validationResults || Object.keys(validationResults).length === 0) && !isSearching && validationLoadingState !== 'pending' && !searchError" class="flex flex-col items-center justify-center h-[calc(100vh-18em)] gap-4">
      <div class="w-fit h-fit squircle-15">
      <Button
        @click="() => refresh(false)"
        size="lg"
        class="flex items-center gap-2 px-6 py-3"
        :disabled="isRefreshing || isSearching"
      >
        <Icon 
          v-if="isRefreshing || isSearching"
          name="line-md:loading-loop" 
          class="w-5 h-5"
        />
        <Icon 
          v-else
          name="lsicon:lightning-filled" 
          class="w-5 h-5"
        />
        {{ isRefreshing || isSearching ? 'Searching...' : 'Check Availability' }}
      </Button>
    </div>
      <p class="text-sm text-muted-foreground text-center max-w-md">
        Click to search for plant availability across suppliers
      </p>
    </div>

    <!-- Main Content -->
    <ScrollArea 
      v-if="validationResults && Object.keys(validationResults).length > 0"
      ref="scrollAreaRef"
      class="h-[calc(100vh-18em)] w-full scroll-container"
    >
      <div class="flex flex-col gap-8 px-2 pb-8">

        <!-- Availability Summary Section -->
        <section
          id="summary"
          ref="summarySection"
          class="scroll-mt-20"
        >
          <div class="flex justify-between items-start pb-6 px-6 py-4">
            <div>
              <h3 
                class="text-md font-[600] mb-2 group hover:cursor-pointer relative"
                @click="() => refresh(false)"
              >
                <Icon 
                  v-if="isRefreshing" 
                  name="line-md:loading-loop" 
                  class="w-4 h-4 absolute left-1 transition-all duration-200 mr-2"
                />
                <Icon 
                  v-else
                  name="lsicon:lightning-filled" 
                  class="w-4 h-4 absolute opacity-0 group-hover:opacity-100 -translate-x-5 group-hover:translate-x-1 transition-all duration-200 text-muted-foreground mr-2 top-1"
                />
                <div class="relative transition-all duration-200 font-intervariable pl-0" :class="[isRefreshing ? 'translate-x-6' : 'group-hover:translate-x-6']">Availability Summary</div>
              </h3>
              <p class="text-sm text-muted-foreground max-w-2xl">
                Summary of plant availability status showing which species are available from suppliers and which are not currently available.
              </p>
            </div>
          </div>

          <div v-if="validationResults && Object.keys(validationResults).length > 0" class="px-6">
            <!-- Summary Statistics -->
            <div class="flex flex-wrap gap-4 mb-6">
              <div class="bg-background rounded-lg p-4 shadow-sm flex-1 min-w-[180px]">
                <div class="text-sm text-muted-foreground">Available</div>
                <div class="text-2xl font-semibold text-emerald-500">{{ availabilityStats.availableCount }}</div>
                <div class="text-xs text-muted-foreground">{{ availabilityStats.availablePercentage }}% of total</div>
              </div>
              <div class="bg-background rounded-lg p-4 shadow-sm flex-1 min-w-[180px]">
                <div class="text-sm text-muted-foreground">Unavailable</div>
                <div class="text-2xl font-semibold text-rose-500">{{ availabilityStats.unavailableCount }}</div>
                <div class="text-xs text-muted-foreground">{{ (100 - availabilityStats.availablePercentage) }}% of total</div>
              </div>
            </div>

            <!-- Available Plants -->
            <div class="mb-6" v-if="getAvailableSpecies.length > 0">
              <Accordion type="single" collapsible class="w-full" :default-value="'available'">
                <AccordionItem value="available">
                  <AccordionTrigger class="text-base font-medium hover:no-underline" icon>
                    <div class="flex items-center gap-2">
                      <span class="text-emerald-500">Available Plants</span>
                    <Badge variant="outline">{{ getAvailableSpecies.length }}</Badge>
                  </div>
                </AccordionTrigger>
              
                <AccordionContent>
                  <div class="flex flex-wrap gap-4 mt-4">
                    <div 
                      v-for="item in getAvailableSpecies" 
                      :key="item.name"
                      class="bg-background rounded-lg p-4 transition-all hover:shadow-md group flex-grow basis-[240px] min-w-[200px]"
                    >
                      <div class="flex justify-between items-start">
                        <h4 class="font-medium text-base">{{ item.name }}</h4>
                        <Badge class="bg-emerald-500 text-white">Available</Badge>
                      </div>
                      <div class="text-sm text-muted-foreground mt-1">
                        {{ item.result.suppliers?.length || 0 }} supplier{{ item.result.suppliers?.length !== 1 ? 's' : '' }}
                      </div>
                      <div v-if="item.result.suppliers && item.result.suppliers.length > 0" class="mt-2">
                        <div class="text-xs font-medium text-muted-foreground uppercase mt-2">Price Range</div>
                        <div class="flex items-center gap-2">
                          <span class="text-sm font-medium">
                            {{ formatPrice(Math.min(...(item.result.suppliers || []).map(s => s.price))) }}
                            <span class="text-muted-foreground">-</span>
                            {{ formatPrice(Math.max(...(item.result.suppliers || []).map(s => s.price))) }}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
              </Accordion>
            </div>

            <!-- Unavailable Plants -->
            <div v-if="getUnavailableSpecies.length > 0">
              <Accordion type="single" collapsible class="w-full" :default-value="'unavailable'">
                <AccordionItem value="unavailable">
                  <AccordionTrigger class="text-base font-medium hover:no-underline" icon>
                    <div class="flex items-center gap-2">
                      <span class="text-rose-500">Unavailable Plants</span>
                      <Badge variant="outline">{{ getUnavailableSpecies.length }}</Badge>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div class="flex flex-wrap gap-4 mt-4">
                      <div 
                        v-for="item in getUnavailableSpecies" 
                        :key="item.name"
                        class="bg-background rounded-lg p-4 transition-all hover:shadow-md group flex-grow basis-[240px] min-w-[200px]"
                      >
                        <div class="flex justify-between items-start mb-2">
                          <h4 class="font-medium text-base">{{ item.name }}</h4>
                          <Badge variant="destructive" class="whitespace-nowrap">Unavailable</Badge>
                        </div>
                        <div class="text-sm text-muted-foreground mt-1">
                          No suppliers found
                        </div>
                        <div class="text-xs text-muted-foreground mt-2">
                          Last checked: {{ formatDate(String(item.result.lastChecked || new Date())) }}
                        </div>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </div>
          </div>
        </section>

        <!-- TreeMap Section -->
        <section 
          id="treemap"
          ref="treemapSection"
          class="scroll-mt-20"
          v-if="getAvailableSpecies.length"
        >
          <div class="flex justify-between items-start pb-6 px-6 py-4">
            <div>
              <h3 
                class="text-md font-[600] mb-2 group hover:cursor-pointer relative"
                @click="() => refresh(false)"
              >
                <Icon 
                  v-if="isRefreshing" 
                  name="line-md:loading-loop" 
                  class="w-4 h-4 absolute left-1 transition-all duration-200 mr-2"
                />
                <Icon 
                  v-else
                  name="lsicon:lightning-filled" 
                  class="w-4 h-4 absolute opacity-0 group-hover:opacity-100 -translate-x-5 group-hover:translate-x-1 transition-all duration-200 text-muted-foreground mr-2 top-1"
                />
                <div class="relative transition-all duration-200 pl-0" :class="[isRefreshing ? 'translate-x-6' : 'group-hover:translate-x-6']">Plant Availability</div>
              </h3>
              
            </div>
            <div class="flex gap-2">
              <!-- Stop Search button -->
              <Button 
                v-if="isSearching"
                size="sm"
                variant="destructive"
                @click="stopSearch"
              >
                Stop Search
              </Button>
              
              <!-- Add Apply Results button -->
              <Button 
                v-if="validationResults && Object.keys(validationResults).length > 0 && panel_id"
                size="sm"
                variant="outline"
                @click="applyValidatedResults"
              >
                Apply Results
              </Button>
            </div>
          </div>

          <div v-if="validationResults && Object.keys(validationResults).length > 0" class="bg-background rounded-lg p-6 focus-within:ring-2 focus-within:ring-primary focus-within:ring-opacity-50 transition-all duration-200 mx-6">
            <AvailabilityTreeMap :data="validationResults" @selection="handleTreeMapSelect" />
          </div>
        </section>

        <!-- Euler Section -->
        <section
          v-if="validationResults && Object.keys(validationResults).length > 0 && getAvailableSpecies.length > 0" 
          id="euler"
          ref="eulerSection"
          class="scroll-mt-20"
        >
          <div class="flex justify-between items-start pb-6 px-6 py-4">
            <div>
              <h3 
                class="text-md font-[600] mb-2 group hover:cursor-pointer relative"
                @click="() => refresh(false)"
              >
                <Icon 
                  v-if="isRefreshing" 
                  name="line-md:loading-loop" 
                  class="w-4 h-4 absolute left-1 transition-all duration-200 mr-2"
                />
                <Icon 
                  v-else
                  name="lsicon:lightning-filled" 
                  class="w-4 h-4 absolute opacity-0 group-hover:opacity-100 -translate-x-5 group-hover:translate-x-1 transition-all duration-200 text-muted-foreground mr-2 top-1"
                />
                <div class="relative transition-all duration-200 pl-0" :class="[isRefreshing ? 'translate-x-6' : 'group-hover:translate-x-6']">Relationships</div>
              </h3>
              <p class="text-sm text-muted-foreground max-w-2xl">
                Visualizes the relationships between plant species and their shared suppliers. 
                Overlapping areas indicate suppliers that stock multiple species, helping identify cross-availability patterns. Each circle represents a plant species. Numbers in circles show shared suppliers between species.
              </p>
            </div>
          </div>

          <div v-if="validationResults && Object.keys(validationResults).length > 0" class="bg-background rounded-lg p-6 focus-within:ring-2 focus-within:ring-primary focus-within:ring-opacity-50 transition-all duration-200 mx-6">
            <AvailabilityEulerDiagram :data="validationResults" />
          </div>
        </section>

        <!-- Scatter Section -->
        <section
          v-if="validationResults && Object.keys(validationResults).length > 0 && getAvailableSpecies.length > 0" 
          id="scatter"
          ref="scatterSection"
          class="scroll-mt-20"
        >
          <div class="flex justify-between items-start pb-6 px-6 py-4">
            <div>
              <h3 
                class="text-md font-[600] mb-2 group hover:cursor-pointer relative"
                @click="() => refresh(false)"
              >
                <Icon 
                  v-if="isRefreshing" 
                  name="line-md:loading-loop" 
                  class="w-4 h-4 absolute left-1 transition-all duration-200 mr-2"
                />
                <Icon 
                  v-else
                  name="lsicon:lightning-filled" 
                  class="w-4 h-4 absolute opacity-0 group-hover:opacity-100 -translate-x-5 group-hover:translate-x-1 transition-all duration-200 text-muted-foreground mr-2 top-1"
                />
                <div class="relative transition-all duration-200 pl-0" :class="[isRefreshing ? 'translate-x-6' : 'group-hover:translate-x-6']">Analysis</div>
              </h3>
              <p class="text-sm text-muted-foreground max-w-2xl">
                Visualizes the correlation between price and quantity across different suppliers. 
                The scatter plot helps identify pricing patterns and potential volume discounts, 
                with each point representing a plant species.
              </p>
            </div>
          </div>

          <div v-if="validationResults && Object.keys(validationResults).length > 0" class="bg-background rounded-lg p-6 focus-within:ring-2 focus-within:ring-primary focus-within:ring-opacity-50 transition-all duration-200 mx-6">
            <PriceQuantityScatter :data="validationResults" />
          </div>
        </section>
      </div>
    </ScrollArea>
  </div>
</template>

<style scoped>
.sticky-header {
  min-height: 52px;
  display: flex;
  align-items: center;
}

.scroll-mt-20 {
  scroll-margin-top: 5rem;
}

.backdrop-blur-sm {
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}

:deep(.echarts-tooltip-custom:not(:has(.tooltip-content))) {
  display: none !important;
}
</style>

